import { parseHTML, replaceElement } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

/**
 * Creates a dynamic part of a section.
 *
 * @attribute event - The event which triggers an update.
 * @attribute section - The source section.
 * @attribute update-mode - The method of updating elements.
 * @attribute key - If defined, updates will only happen if this differs.
 */
export class SectionFragment extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (!this.id) return;

    this.subscribe(this.getAttribute('event'), (event) => {
      const section = parseHTML(event.sections[this.getAttribute('section')] ?? '');
      const element = section.getElementById(this.id);

      if (!element) return this.remove();

      /**
       * Updates the attributes and content, whilst retaining the original element.
       */
      if (this.getAttribute('update-mode') === 'contents') {
        this.updateElementAttributes(this.firstElementChild, element.firstElementChild);

        this.firstElementChild.innerHTML = element.firstElementChild.innerHTML;
        return;
      }

      if (this.getAttribute('update-mode') === 'inputs') {
        const updatedInputs = [...element.querySelectorAll('input')];

        [...this.querySelectorAll('input')].forEach((input, index) => {
          this.updateElementAttributes(input, updatedInputs[index]);
        });

        return;
      }

      if (this.hasAttribute('key')) {
        if (!(this.getAttribute('key') === element.getAttribute('key'))) {
          replaceElement(this, element);
        }

        return;
      }

      replaceElement(this, element);
    });
  }

  /**
   * Updates an element's attributes.
   * @param {HTMLElement} element - The existing element.
   * @param {HTMLElement} updated - The updated element.
   */
  updateElementAttributes(element, updated) {
    for (const { name, value } of updated.attributes) {
      element.setAttribute(name, value);
    }

    /**
     * Handles properties which need to be manually updated.
     */
    ['value', 'checked', 'selected', 'disabled', 'readOnly', 'className'].forEach(
      (key) => (element[key] = updated[key])
    );
  }
}
